import { 
  IconButton, 
  SvgIcon, 
  FormLabel, 
  Button, 
  Tooltip
} from '@mui/material';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { borderRadius, palette, spaces } from '../../../../../assets/styles/themes/tokens';
import getMuiIcon from '../../../../../utils/getMuiIcon';
import { aiAssistantContext } from '../../../../../state/AIAssistantContextProvider';
import { v4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import useAction from '../../../../../utils/useAction';
import {
  getAttachmentsRemainingLimit as getAttachmentsRemainingLimitAction,
  uploadAttachment as uploadAttachmentAction
} from '../../../../../actions/aiAssistant.actions';
import { useSelector } from 'react-redux';
import {
  getCurrentConversation,
  getTemporaryAttachments,
  getAttachmentsRemainingLimit as getAttachmentsRemainingLimitSelector,
} from '../../../../../reducers/aiAssistant.selectors';
import { AsyncActionStatus } from '../../../../../actions/common.actionTypes';
import {
  MAX_ATTACHMENTS_PER_REQUEST,
  MAX_ATTACHMENTS_PER_CONVERSATION,
  MAX_UPLOADS_PER_DAY,
} from '../../constants';

const inputId = 'ai-assistant-attachments-input';

const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
const documentExtensions = ['pdf', 'md', 'doc', 'docx', 'txt', 'xls', 'xlsx'];

const allowedExtensions = [...imageExtensions, ...documentExtensions];

const errorCodes = {
  NOT_SUPPORTED_FILE_TYPE: 'NOT_SUPPORTED_FILE_TYPE',
  IMAGE_RESOLUTION_TOO_LARGE: 'IMAGE_RESOLUTION_TOO_LARGE',
  IMAGE_SIZE_TOO_LARGE: 'IMAGE_SIZE_TOO_LARGE',
  PDF_PARSING_ERROR: 'PDF_PARSING_ERROR',
  PDF_PAGES_COUNT_TOO_LARGE: 'PDF_PAGES_COUNT_TOO_LARGE',
};

const MAX_FILE_SIZE_MB = 4.5;

type AttachmentsButtonProps = {
  fromControlledFieldScreen?: boolean;
  label?: string;
};

const AttachmentsButton: React.FC<AttachmentsButtonProps> = ({ fromControlledFieldScreen, label }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { 
    aiFileUploadAbortControllerRef, 
    setAttachmentUploadError,
    setAttachmentInfoMessage 
  } = useContext(aiAssistantContext);
  const [isUploading, setIsUploading] = useState(false);
  const currentConversation = useSelector(getCurrentConversation);
  const isConversationLoading = currentConversation?.status === AsyncActionStatus.LOADING;

  const currentAttachmentCount = useMemo(() => {
    if (!currentConversation || !currentConversation.data) {
      return 0;
    }

    return currentConversation.data.messages.reduce((acc, message) => {
      if (message.role === 'user') {
        return acc + (message.context?.attachments?.length ?? 0);
      }
      return acc;
    }, 0);

  }, [currentConversation]);

  const uploadAttachment = useAction(uploadAttachmentAction);
  const getAttachmentsRemainingLimit = useAction(getAttachmentsRemainingLimitAction);
  const temporaryAttachments = useSelector(getTemporaryAttachments);
  const attachmentsRemainingLimit = useSelector(getAttachmentsRemainingLimitSelector);

  const shouldBeDisabled = isUploading || isConversationLoading;

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (shouldBeDisabled) {
      return;
    }

    if (
      typeof attachmentsRemainingLimit === 'number' &&
      (attachmentsRemainingLimit - temporaryAttachments.length <= 0)
    ) {
      setAttachmentInfoMessage(t(
        'You\'ve reached the upload limit of {{count}} uploads per 24 hours. You can upload files again tomorrow.',
        { count: MAX_UPLOADS_PER_DAY }
      ));
      return;
    }

    if (temporaryAttachments.length >= MAX_ATTACHMENTS_PER_REQUEST - 1) {
      setAttachmentInfoMessage(t(
        'You can upload up to {{count}} files per input.', { count: MAX_ATTACHMENTS_PER_REQUEST }
      ));
      if (temporaryAttachments.length >= MAX_ATTACHMENTS_PER_REQUEST) {
        // If the limit is reached, don't allow any more uploads
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        return;
      }
    }

    if (
      temporaryAttachments.length + currentAttachmentCount >= MAX_ATTACHMENTS_PER_CONVERSATION
    ){
      setAttachmentInfoMessage(t(
        'No further files can be uploaded in this conversation.',
        { count: MAX_ATTACHMENTS_PER_CONVERSATION }
      ));
      return;
    }

    const files = e.target.files;
    const attachment = files?.[0];

    if (attachment) {
      setIsUploading(true);
      const hashName = v4(); 
      if (aiFileUploadAbortControllerRef && !aiFileUploadAbortControllerRef.current) {
        aiFileUploadAbortControllerRef.current = new AbortController();
      }

      try {
        await uploadAttachment({
          temporaryAttachmentHashName: hashName,
          attachment,
          abortSignal: aiFileUploadAbortControllerRef?.current?.signal
        });
      } catch (error: any) {
        setAttachmentInfoMessage(null);
        const limit = error?.data?.data?.limit;

        if (error?.data?.statusCode === 413 && !limit) {
          setAttachmentUploadError(t(
            'File size too large ({{limit}}MB).',
            { limit: MAX_FILE_SIZE_MB }
          ));
        } else {
          switch (error?.data?.code) {
            case errorCodes.NOT_SUPPORTED_FILE_TYPE:
              setAttachmentUploadError(t(
                'File type not supported. Please use one of the following formats: {{extensions}}.',
                { extensions: limit.join(', ').toUpperCase() }
              ));
              break;
            case errorCodes.IMAGE_RESOLUTION_TOO_LARGE:
              setAttachmentUploadError(t(
                'Image resolution too large. Maximum allowed: {{limit}}x{{limit}} pixels. ' +
                'Please resize your image and try again.',
                { limit }
              ));
              break;
            case errorCodes.IMAGE_SIZE_TOO_LARGE:
              setAttachmentUploadError(t(
                'Image file too large. Maximum allowed: {{limit}}MB. Please compress your image and try again.',
                { limit: limit / 1024 / 1024 }
              ));
              break;
            case errorCodes.PDF_PARSING_ERROR:
              setAttachmentUploadError(t(
                'PDF could not be processed. The file might be corrupted, password-protected, ' +
                'or in an unsupported format. Please try a different PDF file.'
              ));
              break;
            case errorCodes.PDF_PAGES_COUNT_TOO_LARGE:
              setAttachmentUploadError(t(
                'PDF has too many pages. Maximum allowed: {{limit}} pages. ' +
                'Please split your PDF or use a shorter document.',
                { limit }
              ));
              break;
          }
        }
      } finally {
        if (aiFileUploadAbortControllerRef?.current) {
          aiFileUploadAbortControllerRef.current = null;
        }
        setIsUploading(false);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }
    }
  };

  const handleClick = () => {
    getAttachmentsRemainingLimit();
    if (inputRef.current && !shouldBeDisabled) {
      inputRef.current.click();
    }
  };

  useEffect(() => {
    if (attachmentsRemainingLimit === null) {
      getAttachmentsRemainingLimit();
    }
  }, [attachmentsRemainingLimit, getAttachmentsRemainingLimit]);

  useEffect(() => {
    if (temporaryAttachments.length === 0) {
      setAttachmentInfoMessage(null);
    }
  }, [temporaryAttachments, setAttachmentInfoMessage]);

  return (
    <>
      <input 
        type='file' 
        hidden 
        id={inputId}
        data-testid={inputId}
        ref={inputRef} 
        onChange={handleChange} 
        disabled={shouldBeDisabled}
        accept={allowedExtensions.map(ext => `.${ext}`).join(',')}
      />
      {fromControlledFieldScreen ? 
        <Tooltip title={t('Add file')} placement='bottom' arrow>
          <Button
            data-testid='ai-file-upload-from-scratch-button'
            component='label'
            variant='outlined'
            onClick={handleClick}
            aria-label={label || t('Attachments')}
            aria-disabled={shouldBeDisabled}
            disabled={shouldBeDisabled}
            sx={{
              border: `2px solid ${palette.components.input.outlined.enabledBorder}`,
              borderRadius: borderRadius.sm,
              color: palette.common.black,
              width: '100%',
              backgroundColor: palette.background.paperElevation1,
              '&:hover': {
                border: `2px solid ${palette.components.input.outlined.enabledBorder}`,
                boxShadow: `inset 0 0 0 1px ${palette.common.black}`,
                backgroundColor: palette.background.paperElevation0,
              },
            }}
            startIcon={
              <SvgIcon
                sx={{ color: palette.common.black, transform:'rotate(40deg)' }}
                component={getMuiIcon('paperclipStraight')}
                aria-hidden='true'
              />
            }
          >
            {label}
          </Button>
        </Tooltip>
        :
        <FormLabel component='label' htmlFor={inputId} sx={{ margin: 0 }}>
          <Tooltip title={t('Add file')} placement='bottom' arrow>
            <IconButton
              disabled={shouldBeDisabled}
              type='button'
              data-testid='ai-assistant-attachments-button'
              color='outline'
              size='small'
              sx={{ padding: spaces.xxs }}
              onClick={handleClick}
              aria-label={t('Attachments')}
              aria-disabled={shouldBeDisabled}
            >
              <SvgIcon
                sx={{ width: 24, height: 24 }}
                viewBox='0 0 24 24'
                component={getMuiIcon('paperclipStraight')}
                aria-hidden='true'
              />
            </IconButton>
          </Tooltip>
        </FormLabel>
      }
    </>
  );
};

export default AttachmentsButton;
